import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import './AdminDashboard.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

interface User {
  id: number;
  email: string;
  full_name: string;
  role: string;
  auth_provider: string;
  two_factor_enabled: boolean;
  created_at: string;
  last_login: string | null;
}

interface Invitation {
  id: number;
  email: string;
  token: string;
  role: string;
  invite_url: string;
  expires_at: string;
  used: boolean;
  used_at: string | null;
}

const AdminDashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [activeTab, setActiveTab] = useState<'users' | 'invites' | 'apikeys'>('users');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Invite form
  const [emailList, setEmailList] = useState('');
  const [inviteRole, setInviteRole] = useState('user');
  const [inviteExpiry, setInviteExpiry] = useState(7);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [createdInvites, setCreatedInvites] = useState<Invitation[]>([]);

  // API keys
  const [apiKeys, setApiKeys] = useState<any[]>([]);
  const [showApiKeyForm, setShowApiKeyForm] = useState(false);
  const [apiKeyName, setApiKeyName] = useState('');
  const [apiKeyDescription, setApiKeyDescription] = useState('');
  const [createdApiKey, setCreatedApiKey] = useState<any>(null);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    loadData();
  }, [user, navigate]);

const loadData = async () => {
  setLoading(true);
  try {
    const token = localStorage.getItem('token');
    const config = { headers: { Authorization: `Bearer ${token}` } };
    
    const [usersRes, invitesRes, apiKeysRes] = await Promise.all([
      axios.get(`${API_URL}/admin/users`, config),
      axios.get(`${API_URL}/admin/invitations`, config),
      axios.get(`${API_URL}/admin/oauth2-clients`, config)
    ]);
    
    setUsers(usersRes.data.users);
    setInvitations(invitesRes.data.invitations);
    setApiKeys(apiKeysRes.data.clients);
  } catch (err: any) {
    setError(err.response?.data?.detail || 'Failed to load data');
  } finally {
    setLoading(false);
  }
};

  const handleCreateInvites = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    // Parse emails from textarea
    const emails = emailList
      .split(/[\n,;]/)
      .map(e => e.trim())
      .filter(e => e.length > 0);
    
    if (emails.length === 0) {
      setError('Please enter at least one email address');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/admin/invitations`,
        {
          emails: emails,
          role: inviteRole,
          expires_in_days: inviteExpiry
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setCreatedInvites(response.data);
      setEmailList('');
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create invitations');
    }
  };

  const handleDeleteUser = async (userId: number) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleToggleRole = async (userId: number, currentRole: string) => {
    const newRole = currentRole === 'admin' ? 'user' : 'admin';
    
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API_URL}/admin/users/${userId}`,
        { role: newRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleDeleteInvite = async (inviteId: number) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/admin/invitations/${inviteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete invitation');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  if (loading) {
    return <div className="admin-loading">Loading...</div>;
  }

  return (
    <div className="admin-dashboard">
      <nav className="admin-nav">
        <h1>🔧 Admin Panel</h1>
        <div>
          <button onClick={() => navigate('/dashboard')}>← Back to Dashboard</button>
          <span>{user?.email}</span>
          <button onClick={logout}>Logout</button>
        </div>
      </nav>

      {error && (
        <div className="admin-error">
          {error}
          <button onClick={() => setError('')}>×</button>
        </div>
      )}

      <div className="admin-tabs">
        <button 
          className={activeTab === 'users' ? 'active' : ''} 
          onClick={() => setActiveTab('users')}
        >
          Users ({users.length})
        </button>
        <button 
          className={activeTab === 'invites' ? 'active' : ''} 
          onClick={() => setActiveTab('invites')}
        >
          Invitations ({invitations.length})
        </button>
<button 
  className={activeTab === 'apikeys' ? 'active' : ''} 
  onClick={() => setActiveTab('apikeys')}
>
  API Keys ({apiKeys.length})
</button>
      </div>

      <div className="admin-content">
        {activeTab === 'users' && (
          <div className="users-section">
            <h2>User Management</h2>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Role</th>
                  <th>Provider</th>
                  <th>2FA</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id}>
                    <td>{u.email}</td>
                    <td>{u.full_name}</td>
                    <td>
                      <span className={`role-badge ${u.role}`}>{u.role}</span>
                    </td>
                    <td>{u.auth_provider}</td>
                    <td>{u.two_factor_enabled ? '✅' : '❌'}</td>
                    <td>{new Date(u.created_at).toLocaleDateString()}</td>
                    <td>
                      <button 
                        onClick={() => handleToggleRole(u.id, u.role)}
                        disabled={u.id === user?.id}
                      >
                        {u.role === 'admin' ? '⬇️ Demote' : '⬆️ Promote'}
                      </button>
                      <button 
                        onClick={() => handleDeleteUser(u.id)}
                        disabled={u.id === user?.id}
                        className="delete-btn"
                      >
                        🗑️ Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'invites' && (
          <div className="invites-section">
            <div className="invites-header">
              <h2>Invitation Management</h2>
              <button 
                onClick={() => setShowInviteForm(!showInviteForm)}
                className="primary-btn"
              >
                {showInviteForm ? '✖ Cancel' : '✉️ Create Invitations'}
              </button>
            </div>

            {showInviteForm && (
              <div className="invite-form">
                <h3>Create Bulk Invitations</h3>
                <form onSubmit={handleCreateInvites}>
                  <div className="form-group">
                    <label>Email Addresses (one per line, or comma-separated)</label>
                    <textarea
                      value={emailList}
                      onChange={(e) => setEmailList(e.target.value)}
                      placeholder="user1@example.com&#10;user2@example.com&#10;user3@example.com"
                      rows={6}
                      required
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Role</label>
                      <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}>
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Expires in (days)</label>
                      <input
                        type="number"
                        value={inviteExpiry}
                        onChange={(e) => setInviteExpiry(Number(e.target.value))}
                        min="1"
                        max="365"
                      />
                    </div>
                  </div>

                  <button type="submit" className="primary-btn">
                    Create Invitations
                  </button>
                </form>

                {createdInvites.length > 0 && (
                  <div className="created-invites">
                    <h4>✅ Created {createdInvites.length} Invitation(s)</h4>
                    {createdInvites.map(inv => (
                      <div key={inv.id} className="invite-result">
                        <strong>{inv.email}</strong>
                        <button onClick={() => copyToClipboard(inv.invite_url)}>
                          📋 Copy Link
                        </button>
                        <code>{inv.invite_url}</code>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <h3>Pending Invitations</h3>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Expires</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {invitations.map(inv => (
                  <tr key={inv.id}>
                    <td>{inv.email}</td>
                    <td><span className={`role-badge ${inv.role}`}>{inv.role}</span></td>
                    <td>{new Date(inv.expires_at).toLocaleDateString()}</td>
                    <td>
                      <button onClick={() => copyToClipboard(inv.invite_url)}>
                        📋 Copy Link
                      </button>
                      <button 
                        onClick={() => handleDeleteInvite(inv.id)}
                        className="delete-btn"
                      >
                        🗑️ Delete
                      </button>
                    </td>
                  </tr>
                ))}
                {invitations.length === 0 && (
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'center', padding: '20px' }}>
                      No pending invitations
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
{activeTab === 'apikeys' && (
  <div className="apikeys-section">
    <div className="invites-header">
      <h2>API Key Management</h2>
      <button 
        onClick={() => setShowApiKeyForm(!showApiKeyForm)}
        className="primary-btn"
      >
        {showApiKeyForm ? '✖ Cancel' : '🔑 Create API Key'}
      </button>
    </div>

    {showApiKeyForm && (
      <div className="invite-form">
        <h3>Create New API Key</h3>
        <form onSubmit={async (e) => {
          e.preventDefault();
          try {
            const token = localStorage.getItem('token');
            const response = await axios.post(
              `${API_URL}/admin/oauth2-clients`,
              { name: apiKeyName, description: apiKeyDescription },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            setCreatedApiKey(response.data);
            setApiKeyName('');
            setApiKeyDescription('');
            setShowApiKeyForm(false);
            loadData();
          } catch (err: any) {
            setError(err.response?.data?.detail || 'Failed to create API key');
          }
        }}>
          <div className="form-group">
            <label>Name</label>
            <input
              type="text"
              value={apiKeyName}
              onChange={(e) => setApiKeyName(e.target.value)}
              placeholder="My API Client"
              required
            />
          </div>
          <div className="form-group">
            <label>Description (optional)</label>
            <input
              type="text"
              value={apiKeyDescription}
              onChange={(e) => setApiKeyDescription(e.target.value)}
              placeholder="Used for CSV uploads from our system"
            />
          </div>
          <button type="submit" className="primary-btn">
            Create API Key
          </button>
        </form>
      </div>
    )}

    {createdApiKey && (
      <div className="created-invites" style={{ background: '#fff3cd', borderLeft: '4px solid #ffc107' }}>
        <h4>⚠️ API Key Created - Save These Credentials!</h4>
        <p style={{ color: '#856404', marginBottom: '15px' }}>
          The client secret will only be shown once. Save it securely!
        </p>
        <div style={{ background: 'white', padding: '15px', borderRadius: '4px', marginBottom: '10px' }}>
          <strong>Client ID:</strong><br />
          <code style={{ fontSize: '14px', wordBreak: 'break-all' }}>{createdApiKey.client_id}</code>
          <button 
            onClick={() => navigator.clipboard.writeText(createdApiKey.client_id)}
            style={{ marginLeft: '10px', padding: '4px 8px' }}
          >
            📋 Copy
          </button>
        </div>
        <div style={{ background: 'white', padding: '15px', borderRadius: '4px' }}>
          <strong>Client Secret:</strong><br />
          <code style={{ fontSize: '14px', wordBreak: 'break-all', color: '#d63031' }}>
            {createdApiKey.client_secret}
          </code>
          <button 
            onClick={() => navigator.clipboard.writeText(createdApiKey.client_secret)}
            style={{ marginLeft: '10px', padding: '4px 8px' }}
          >
            📋 Copy
          </button>
        </div>
        <button 
          onClick={() => setCreatedApiKey(null)}
          style={{ marginTop: '15px' }}
          className="primary-btn"
        >
          I've Saved the Credentials
        </button>
      </div>
    )}

    <h3>Active API Keys</h3>
    <table className="admin-table">
      <thead>
        <tr>
          <th>Name</th>
          <th>Client ID</th>
          <th>Description</th>
          <th>Status</th>
          <th>Created</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {apiKeys.map(key => (
          <tr key={key.id}>
            <td><strong>{key.name}</strong></td>
            <td><code style={{ fontSize: '12px' }}>{key.client_id}</code></td>
            <td>{key.description || '-'}</td>
            <td>
              <span style={{ 
                padding: '4px 8px', 
                borderRadius: '4px', 
                background: key.is_active ? '#d4edda' : '#f8d7da',
                color: key.is_active ? '#155724' : '#721c24'
              }}>
                {key.is_active ? 'Active' : 'Inactive'}
              </span>
            </td>
            <td>{new Date(key.created_at).toLocaleDateString()}</td>
            <td>
              <button
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('token');
                    await axios.patch(
                      `${API_URL}/admin/oauth2-clients/${key.id}/toggle`,
                      {},
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    loadData();
                  } catch (err: any) {
                    setError('Failed to toggle API key');
                  }
                }}
              >
                {key.is_active ? '⏸ Disable' : '▶️ Enable'}
              </button>
              <button
                onClick={async () => {
                  if (!window.confirm('Delete this API key?')) return;
                  try {
                    const token = localStorage.getItem('token');
                    await axios.delete(
                      `${API_URL}/admin/oauth2-clients/${key.id}`,
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    loadData();
                  } catch (err: any) {
                    setError('Failed to delete API key');
                  }
                }}
                className="delete-btn"
              >
                🗑️ Delete
              </button>
            </td>
          </tr>
        ))}
        {apiKeys.length === 0 && (
          <tr>
            <td colSpan={6} style={{ textAlign: 'center', padding: '20px' }}>
              No API keys created yet
            </td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
)}
      </div>
    </div>
  );
};

export default AdminDashboard;
